import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL;

//Creates a pre-configured axios instance that sends JSON to backend.
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});


// Add request interceptor to include auth token
//Interceptors are functions that run before or after requests and responses.
//This interceptor adds the auth token to the request headers.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const userService = {
    signup: (email, password) => {
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      
      // Check if user exists
      if (users.some(user => user.email === email)) {
        throw new Error('User already exists');
      }
  
      // Add new user
      // Stores a new user locally in localStorage.
      const newUser = { email, password };
      users.push(newUser);
      localStorage.setItem('users', JSON.stringify(users));
      return true;
    },
  
    login: async (email, password) => {
      try {
        // Sends credentials to the /auth/login API. If login is successful and a token is returned, stores it in localStorage.
        const response = await api.post('/auth/login', { email, password });
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('isAuthenticated', 'true');
        }
        return response.data;
      } catch (error) {
        console.error('Login failed:', error);
        throw error;
      }
    },
  
    logout: () => {
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
    },
  
    isAuthenticated: () => {
      // Returns true if the user is logged in and a token is stored.
      return localStorage.getItem('isAuthenticated') === 'true' && localStorage.getItem('token');
    },

    // Update OAuth methods to include auth token
    async initiateOAuth() {
      try {
        // debug
        const token = localStorage.getItem('token');
        console.log('Token before OAuth initiation:', token); 

        const response = await api.get('/oauth/initiate');
        console.log('OAuth initiation response:', response.data);
        
        if (response.data.success) {
          // Check if user already has valid tokens
          if (response.data.hasTokens) {
            console.log('User already has valid PG&E access tokens');
            return {
              success: true,
              hasTokens: true,
              message: response.data.message
            };
          }
          
          // User needs to complete OAuth flow
          if (response.data.authorizationUrl) {
            // Redirect to PG&E's authorization page
            console.log('Redirecting to PG&E authorization URL:', response.data.authorizationUrl);
            window.location.href = response.data.authorizationUrl;
            return {
              success: true,
              hasTokens: false,
              redirecting: true,
              message: response.data.message
            };
          }
          // Defensive fallback
          throw new Error('Missing both tokens and authorization URL in OAuth response');
        }
        
        throw new Error(response.data.message || 'Failed to initiate OAuth flow');
      } catch (error) {
        console.error('OAuth initiation error:', error);
        throw error;
      }
    },

    async handleOAuthCallback(code, state) {
      try {
        // const urlParams = new URLSearchParams(window.location.search);
        // const code = urlParams.get('code');

        // Validate parameters
        if (!code) {
          throw new Error('No authorization code provided');
        }
        console.log('Handling OAuth callback with code:', code);
        
        if (!state) {
          throw new Error('No state parameter provided');
        }
        console.log('Handling OAuth callback with state:', state);
        // OAuth provider (PG&E) is redirecting back to the frontend with a query parameter named code, not authorization_code

        console.log('Handling OAuth callback with code and state:', { 
          code: code.substring(0, 8) + '...',
          state: state.substring(0, 8) + '...'
        });
        
        // Sends the OAuth authorization_code received from PG&E back to the server for token exchange.
        // const response = await api.post('/oauth/callback', {
        //   authorization_code: code
        // });
        const response = await api.post('/oauth/callback', {
          authorization_code: code,
          state: state
        });

        return response.data;
      } catch (error) {
        console.error('OAuth callback failed:', error.response?.data || error.message);
        throw new Error(error.response?.data?.message || 'OAuth callback error');
      }
    },

    async getProfile() {
      try {
        const response = await api.get('/users/profile');
        return response.data;
      } catch (error) {
        console.error('Get profile error:', error);
        throw error;
      }
    },

    async checkTokenStatus() {
      try {
        const response = await api.get('/auth/token-status');
        console.log('Token status response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Token status check error:', error);
        throw error;
      }
    }
  };

export { userService };